<script setup lang="ts">
import { getImgSrc, getOpenGraph, getTwitter } from '~/helpers/useMetaData'
import type { Locale } from 'lc-services/types'

definePageMeta({
  layout: 'no-header',
  middleware: ['house'],
  gtmAdditionalEventData: { page_type: 'photos' },
})
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/luxury-rental/[slug]/photos',
    fr: '/location-luxe/[slug]/photos',
  },
})

const nuxtApp = useNuxtApp()
const route = useRoute()
const { back } = useRouter()
const { t, locale } = useI18n<unknown, Locale>({ useScope: 'global' })
const localePath = useLocalePath()
const { trackEvent } = useTracking()
const { isMobile } = useBreakpoint()
const { locked, setLock } = useLockBody(!isMobile.value)
const { isIos, isSafari } = useDevice()

onMounted(() => {
  const fn = scrollIntoView(route.hash)
  if (isIos || isSafari) {
    // Don't ask me WHY, it just works. Otherwise, it scrolls with a negative Y-offset
    // (Probably because some elements still aren't loaded by Safari so the clientHeight is not fully fixed)
    requestAnimationFrame(fn)
  } else {
    fn()
  }
  if (isMobile.value) document.addEventListener('scroll', onScrollEnd)
  else document.addEventListener('keyup', escFunction, true)
})

onUnmounted(() => {
  if (isMobile.value) document.removeEventListener('scroll', onScrollEnd)
  else document.removeEventListener('keyup', escFunction, true)
  // In case the slider does not close properly
  if (locked.value) setLock(false)
})

const { data: houseDataCached, error } = await useFetch(
  `/api/${locale.value}/house/${route.params.slug}`,
  {
    query: route.query['private-token']
      ? { 'private-token': route.query['private-token'] }
      : {},
  },
)

if (error?.value || !houseDataCached.value) {
  throw showError({ statusCode: 404 })
}

const showVirtualVisitModal = ref(false)
const showShareModal = ref(false)
const areaSelected = ref(route.hash.slice(1))

const { houseDetails, houseAreaEquipments, housePhotos } = houseDataCached.value
const houseMatterportVideoUrl = useMatterportUrl(houseDetails)
const {
  allPhotosByArea,
  allHousePhotos,
  closeSlider,
  containerRef,
  galleryRefs,
  hasPhotosByArea,
  hasLoadedAllPhotos,
  fetchHousePhotos,
  onScrollEnd,
  openedIndex,
  openSlider,
  showScrollToTopButton,
  sliderIsOpen,
  totalCount,
} = useHousePhotos({
  areaSelected,
  houseAreaEquipments,
  houseDetails,
  housePhotos,
})

const housePath = computed(() =>
  localePath({
    name: 'house-slug',
    params: route.params,
    query: route.query,
  }),
)
const metaTitle = computed(() =>
  t('house.metaTitle', {
    property_name: houseDetails.name,
    destination: houseDetails.destination?.name,
  }),
)
const metaDescription = computed(() =>
  t('house.meta_description', {
    property_name: houseDetails.name,
    destination: houseDetails.destination?.name,
    bedrooms: houseDetails.bedrooms,
    capacity: houseDetails.capacity,
  }),
)

const openModalVirtualVisit = () => {
  showVirtualVisitModal.value = true
  trackEvent({
    category: 'Product page',
    event: 'virtual_visit_requested',
    house_id: Number(houseDetails.id),
  })
}
const handleOpenSlider = async (photoId: string) => {
  if (photoId === 'virtual-visit') return openModalVirtualVisit()
  await openSlider(photoId)
}

const handleBack = () => {
  if (history.state.back || history.length > 1) back()
  else navigateTo(housePath.value, { replace: true })
}

const scrollToTop = () => {
  if (import.meta.client) window.scrollTo({ top: 0, behavior: 'smooth' })
}
const scrollIntoView = (selector?: string) => () => {
  if (typeof selector !== 'string' || !selector.startsWith('#')) return
  const el = document.querySelector(selector) as HTMLDivElement
  if (el) {
    areaSelected.value = el.id
    el.scrollIntoView({ behavior: 'instant' })
  }
}

const escFunction = async (e: KeyboardEvent) => {
  if (e.key !== 'Escape') return
  if (sliderIsOpen.value) {
    sliderIsOpen.value = false
  } else {
    await navigateTo(housePath.value)
  }
}

nuxtApp.runWithContext(() => {
  useHead({
    title: metaTitle.value,
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: metaDescription.value,
      },
      {
        name: 'robots',
        content: 'noindex',
      },
      ...getOpenGraph(
        metaTitle.value,
        metaDescription.value,
        houseDetails?.firstPhoto?.url,
      ),
      ...getTwitter(
        metaTitle.value,
        metaDescription.value,
        houseDetails?.firstPhoto?.url,
      ),
    ],
    link: [...getImgSrc(houseDetails?.firstPhoto?.url)],
  })
})
</script>

<template>
  <div
    :class="[
      'lg:flex lg:h-screen lg:w-full',
      {
        'flex-col sm:px-16': !hasPhotosByArea,
      },
    ]"
  >
    <NuxtLink
      v-if="!isMobile"
      class="fixed right-6 top-6 z-modal flex size-10 cursor-pointer items-center justify-center rounded-full border-gray-200 bg-white text-gray-700 shadow-[1px_1px_6px_0px_#0000001A]"
      @click="handleBack"
    >
      <BaseIcon name="close" />
    </NuxtLink>

    <div
      :class="[
        'sticky top-0 z-10 bg-white lg:static lg:flex lg:flex-col',
        hasPhotosByArea && !isMobile
          ? 'h-screen w-1/4 min-w-[383px]'
          : 'w-full sm:mt-5 sm:py-5',
      ]"
    >
      <HousePhotosMenu
        v-model:share-modal="showShareModal"
        :house="houseDetails"
        :tag="hasPhotosByArea && !isMobile ? 'aside' : 'header'"
        @on-back="handleBack"
      />

      <HousePhotosAreaLinks
        v-if="!isMobile && hasPhotosByArea"
        v-model="areaSelected"
        class="sm:mt-10 sm:px-10"
        :house="houseDetails"
        :photos-by-area="allPhotosByArea"
      />
    </div>

    <section
      ref="containerRef"
      :class="[
        'relative flex w-full grow flex-col gap-2 overflow-x-hidden sm:scroll-smooth',
        { 'pb-10 sm:pb-0': hasLoadedAllPhotos },
      ]"
      @scroll="onScrollEnd"
    >
      <template v-if="isMobile">
        <h2 class="mt-12 px-4 text-3xl">
          {{ houseDetails.name }}
        </h2>
        <HousePhotosAreaLinks
          v-if="hasPhotosByArea"
          v-model="areaSelected"
          class="mt-5 h-40 min-h-fit px-4"
          :house="houseDetails"
          :photos-by-area="allPhotosByArea"
        />
      </template>
      <article v-else-if="hasPhotosByArea" class="w-full px-4 sm:h-4/5 sm:px-0">
        <BaseNuxtImg
          class="scale-1 cursor-pointer transition duration-500 ease-in-out sm:hover:scale-[1.015]"
          sizes="100vw sm:80vw lg:80vw xl:80vw"
          :alt="houseDetails.name"
          :src="houseDetails.firstPhoto.url"
          @click="handleOpenSlider(houseDetails.firstPhoto.id)"
        />
      </article>

      <template v-if="hasPhotosByArea">
        <article
          v-for="([hash, area], i) in allPhotosByArea"
          :id="hash"
          :key="hash"
          ref="galleryRefs"
          class="flex flex-col gap-2 pt-4 sm:gap-4 sm:pt-6"
        >
          <HousePhotosAreaDescription
            class="px-4 pt-10 sm:px-0 sm:pb-4"
            v-bind="area"
          />

          <HousePhotos
            :house="houseDetails"
            :photos="area.photoGallery.photos"
            :virtual-visit="i === 0 ? houseMatterportVideoUrl : ''"
            @open-slider="handleOpenSlider"
          />
        </article>
      </template>

      <HousePhotos
        v-else
        class="sm:pt-6"
        :house="houseDetails"
        :photos="allHousePhotos"
        :virtual-visit="houseMatterportVideoUrl"
        @open-slider="handleOpenSlider"
      />

      <div
        v-if="!hasLoadedAllPhotos"
        class="relative flex h-24 w-full items-center justify-center py-16"
      >
        <span class="lc-loader__gradient-spin" />
      </div>

      <BaseButton
        v-if="isMobile"
        :class="[
          'fixed bottom-6 right-6 z-sticky !size-10 !rounded-full !border-none !p-0 shadow',
          { '!hidden': !showScrollToTopButton },
        ]"
        color="secondary"
        variant="outline"
        @click="scrollToTop"
      >
        <div class="flex size-full items-center justify-center">
          <BaseIcon name="navArrowUp" />
        </div>
      </BaseButton>
    </section>

    <HouseVirtualVisitModal
      v-model="showVirtualVisitModal"
      :house="houseDetails"
      :matterport-video-url="houseMatterportVideoUrl"
    />

    <HousePhotosSlider
      v-if="sliderIsOpen"
      class-name="house-photos__slider"
      :house="houseDetails"
      :house-photos="allHousePhotos"
      :opened-index="openedIndex"
      :total-count="totalCount"
      @close-slider="closeSlider"
      @slide-change="fetchHousePhotos"
    />

    <HouseShareModal
      v-model="showShareModal"
      :house="houseDetails"
      :house-destinations="houseDetails.destination"
    />

    <WishlistMultipleModal
      :house-id="Number(houseDetails.id)"
      :house-name="houseDetails.name"
      :iconic="houseDetails.iconicCollection"
      :online-booking-available="houseDetails.onlineReservation"
    />
  </div>
</template>

<style scoped>
/* MOBILE
   Add a 'smooth' scroll-behavior to the root / 'html' element
   (as it is the scrolling container)
*/
@media screen and (max-width: 576px) {
  :global(:root) {
    @apply scroll-smooth;
  }
}
</style>
